<template>
    <div class="mt-5 mb-5">
        <div class="container">
            <div class="d-flex flex-wrap justify-content-around">
                <div class="col-12 col-md-6">
                    <div class="mb-4">
                        <base-button
                            tag="a"
                            v-model="payment.brand"
                            v-for="brand in brands[slug]"
                            :type="active_brand===brand ? 'primary' : 'white'"
                            size="sm"
                            href="#"
                            role="button"
                            :class="active_brand===brand ? 'active' : ''"
                            :style="active_brand===brand ? 'pointer-events: none;' : 'pointer-events: auto'"
                            :key="brand"
                            @click="activateBrand(brand)"
                            aria-pressed="true"
                        >
                            <img
                                :src="`/img/${brand}.png`"
                                class="ma-2"
                            >
                        </base-button>
                    </div>
                    <base-input
                        v-model="payment.number"
                        v-mask="'#### #### #### ####'"
                        v-validate="{required:true, regex: /^\d(?:\s?\d){15}$/}"
                        :name="$t('cart.recurrence.credit_card_number')"
                        :placeholder="$t('cart.recurrence.credit_card_number')"
                        :error="errors.first($t('cart.recurrence.credit_card_number'))"
                        @keyup="checkDebitCard"
                        required
                        type="text"
                    ></base-input>
                    <base-input
                        v-model="payment.holder"
                        v-validate="'required'"
                        :name="$t('cart.recurrence.holder')"
                        :placeholder="$t('cart.recurrence.holder')"
                        :error="errors.first($t('cart.recurrence.holder'))"
                        required
                        type="text"
                    ></base-input>
                    <base-input
                        v-model="payment.cvv"
                        v-validate="{ required: true, length:3 }"
                        :name="$t('cart.recurrence.credit_card_cvv')"
                        :placeholder="$t('cart.recurrence.credit_card_cvv')"
                        :error="errors.first($t('cart.recurrence.credit_card_cvv'))"
                        @keyup="validate"
                        v-mask="'###'"
                        required
                        type="number"
                        @mouseover="flipCard"
                        @mouseout="resetCard"
                        @click="flipCard"
                    ></base-input>
                    <div class="row">
                        <base-input
                            v-model="payment.expiration_month"
                            v-validate="{ required: true, regex: /^(0?[1-9]|1[012])$/ }"
                            :name="$t('cart.recurrence.credit_card_expiration_month')"
                            :placeholder="$t('cart.recurrence.credit_card_expiration_month')"
                            :error="errors.first($t('cart.recurrence.credit_card_expiration_month'))"
                            required
                            class="col-12 col-md-6"
                            v-mask="'##'"
                            type="text"
                            @keyup="validate"
                            @blur="payment.expiration_month = payment.expiration_month < 10 ? `0${payment.expiration_month}` : payment.expiration_month;"
                        ></base-input>
                        <base-input
                            v-model="payment.expiration_year"
                            v-validate="{required: true, length:4}"
                            :name="$t('cart.recurrence.credit_card_expiration_year')"
                            :placeholder="$t('cart.recurrence.credit_card_expiration_year')"
                            :error="errors.first($t('cart.recurrence.credit_card_expiration_year'))"
                            @keyup="validate"
                            required
                            class="col-12 col-md-6"
                            v-mask="'####'"
                            type="text"
                        ></base-input>
                    </div>
                </div>
                <div class="col-12 col-md-6 card-container">
                    <div class="front">
                        <span><svg xmlns="http://www.w3.org/2000/svg" style="transform: rotate(90deg);" fill="#ffffff" width="50" height="50" viewBox="0 0 16 16"> <path d="M2 1.5A1.5 1.5 0 0 1 3.5 0h7.086a1.5 1.5 0 0 1 1.06.44l1.915 1.914A1.5 1.5 0 0 1 14 3.414V14.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 14.5v-13zM3.5 1a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V3.414a.5.5 0 0 0-.146-.353l-1.915-1.915A.5.5 0 0 0 10.586 1H3.5z"/> <path d="M5.5 4a.5.5 0 0 0-.5.5V6h2.5V4h-2zm3 0v2H11V4.5a.5.5 0 0 0-.5-.5h-2zM11 7H5v2h6V7zm0 3H8.5v2h2a.5.5 0 0 0 .5-.5V10zm-3.5 2v-2H5v1.5a.5.5 0 0 0 .5.5h2zM4 4.5A1.5 1.5 0 0 1 5.5 3h5A1.5 1.5 0 0 1 12 4.5v7a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 11.5v-7z"/> </svg></span>
                        <div class="card-number-box">
                            {{ payment.number}}
                        </div>
                        <div class="flexbox">
                            <div class="box">
                                <span>CARD HOLDER</span>
                                <div class="card-holder-name">{{ payment.holder }}</div>
                            </div>
                            <div class="box">
                                <span>EXPIRES</span>
                                <div class="expiration">
                                    <span class="exp-month">{{ payment.expiration_month }}</span>
                                    <span class="exp-year">{{ payment.expiration_year }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="back">
                        <div class="stripe"></div>
                        <div class="box">
                            <span>CVV</span>
                            <div class="cvv-box">{{ payment.cvv }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Card from '../../argon/components/Cards/Card';
import BaseInput from '../../argon/components/Inputs/BaseInput';
import BaseButton from '../../argon/components/BaseButton';
import creditCardValidate from '../../helpers/creditCardValidate'

export default {
    emits: ['input', 'update:modelValue', 'validate', 'validateCard'],
    name: 'ChangePaymentDebitCard',
    components: {
        Card,
        BaseInput,
        BaseButton,
    },
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        color: {
            type: Object,
            required: true,
        },
        slug: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            brands: {
                'cielo': [
                    'visa',
                    'mastercard'
                ],
                'cielo-jmv': [
                    'visa',
                    'mastercard'
                ],
                'gerencia-net': [
                    'visa',
                    'mastercard'
                ]
            },
            active_brand: 'visa',
        };
    },

    computed: {
        payment: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
                this.$emit('input', value);
            },
        },
    },

    created() {
        this.payment.brand = this.active_brand;
    },

    methods: {
        flipCard() {
            document.querySelector('.front').style.transform = 'perspective(1000px) rotateY(-180deg)';
            document.querySelector('.back').style.transform = 'perspective(1000px) rotateY(0deg)';
        },
        resetCard() {
            document.querySelector('.front').style.transform = 'perspective(1000px) rotateY(0deg)';
            document.querySelector('.back').style.transform = 'perspective(1000px) rotateY(180deg)';
        },
        activateBrand(brand) {
            this.active_brand = brand;
            this.payment.brand = brand;
        },
        checkDebitCard() {
            this.validateCard();
            const brand = creditCardValidate(this.modelValue.number);

            // BIN desconhecido ou bandeira não oferecida: mantém a seleção manual (backlog#338)
            if (brand && this.brands[this.slug].includes(brand)) {
                this.activateBrand(brand);
            }
        },
        validateCard() {
            this.validate();
            this.$emit('validateCard', this.payment);
        },
        validate() {
            this.$emit('validate');
        },
    },
};
</script>

<style scoped>
.card-container {
    display: flex;
    align-items: center;
    width: 50vw;
}

@media (min-width: 430px) and (max-width: 766px) {
    .card-container {
        margin-top: 108px;
        margin-left: 55px;
        margin-bottom: 70px;
    }
}

@media screen and (max-width: 430px) {
    .card-container {
        margin-top: 108px;
        margin-bottom: 70px;
        margin-right: 60px;
    }
}

.card-container .front{
    position: absolute;
    height: 250px;
    width: 400px;
    background: linear-gradient(45deg, blueviolet, deeppink);
    border-radius: 5px;
    backface-visibility: hidden;
    box-shadow: 0 15px 25px rgba(0, 0, 0, .2);
    padding: 20px;
    transform: perspective(1000px) rotateY(0deg);
    transition: transform .4s ease-out;
}

.card-container .front .card-number-box {
    margin-top: 25px;
    padding: 30px 0;
    font-size: 22px;
    color: #fff;
}

.card-container .front .flexbox {
    display: flex;
}

.card-container .front .flexbox .box:nth-child(1) {
    margin-right: auto;
}

.card-container .front .flexbox .box{
    font-size: 15px;
    color: #fff;
}

.card-container .back{
    position: absolute;
    height: 250px;
    width: 400px;
    background: linear-gradient(45deg, blueviolet, deeppink);
    border-radius: 5px;
    backface-visibility: hidden;
    box-shadow: 0 15px 25px rgba(0, 0, 0, .2);
    padding: 20px 0;
    text-align: right;
    transform: perspective(1000px) rotateY(180deg);
    transition: transform .4s ease-out;
}

.card-container .back .stripe{
    background: #000;
    width: 100%;
    margin: 10px 0;
    height: 50px;
}

.card-container .back .box{
    padding: 0 20px;
    margin-top: 25px;
}

.card-container .back .box span{
    font-size: 15px;
    color: #fff;
}

.card-container .back .box .cvv-box{
    height: 50px;
    padding: 10px;
    margin-top: 5px;
    color: #333;
    background: #fff;
    border-radius: 5px;
    width: 100%;
}

@media only screen and (max-width: 991px) {
    .card-container .front,
    .card-container .back {
        height: 200px;
        width: 320px;
    }
}
</style>
